const DB_NAME = "customizerDB";
const DB_VERSION = 2;
const STORE_NAMES = {
  uploadedImages: "uploadedImages", // For user uploaded images to customizer
  resultImages: "resultImages", // For final result labels generated from customizer 
  onProductImages: "onProductImages", // For product images with result image applied on top
};

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create stores if they don't exist
      if (!db.objectStoreNames.contains(STORE_NAMES.uploadedImages)) {
        db.createObjectStore(STORE_NAMES.uploadedImages, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_NAMES.resultImages)) {
        db.createObjectStore(STORE_NAMES.resultImages, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_NAMES.onProductImages)) {
        db.createObjectStore(STORE_NAMES.onProductImages, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const saveImageToDB = async (
  storeName,
  id,
  imageBlob,
  productId = null,
  resultImageId = null,
  uploadedImageIds = []
) => {
  const arrayBuffer = await imageBlob.arrayBuffer();
  const db = await openDB();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  const data = { id, imageBlob: arrayBuffer };

  if (storeName === "onProductImages") {
    data.productId = productId;
    data.resultImageId = resultImageId;
  }

  if (storeName === "resultImages" && uploadedImageIds.length > 0) {
    data.uploadedImageIds = uploadedImageIds;
  }

  store.put(data);
};

export const getAllFromDB = async (storeName) => {
  const db = await openDB();
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);

  return new Promise((resolve) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => resolve([]);
  });
};

export const getImageFromDB = async (storeName, id, filters = {}) => {
  const db = await openDB();
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);

  return new Promise((resolve) => {
    if (id && storeName !== "onProductImages") {
      const request = store.get(id);

      request.onsuccess = () => {
        if (request.result) {
          resolve(new Blob([request.result.imageBlob], { type: "image/png" }));
        } else {
          resolve(null);
        }
      };

      request.onerror = () => resolve(null);
    } 
    
    // Fetch by productId and resultImageId for onProductImages
    else if (storeName === "onProductImages") {
      const request = store.getAll(); 

      request.onsuccess = () => {
        const images = request.result;
        const matchingImage = images.find((img) => {
          if (filters.productId) {
            return img.productId === filters.productId && img.resultImageId === filters.resultImageId;
          }
          return img.resultImageId === filters.resultImageId;
        });

        resolve(matchingImage ? new Blob([matchingImage.imageBlob], { type: "image/png" }) : null);
      };

      request.onerror = () => resolve(null);
    }
  });
};

export const deleteImageFromDB = async (storeName, id) => {
  const db = await openDB();
  const transaction = db.transaction(storeName, "readwrite");
  transaction.objectStore(storeName).delete(id);
};

/**
 * Clears all records from the specified store.
 * Used in API mode to wipe uploaded images when a new personalization session starts.
 */
export const clearStore = async (storeName) => {
  const db = await openDB();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const resetIndexedDB = async (storeName, cartItems) => {
  const db = await openDB();
  const transaction = db.transaction([STORE_NAMES[storeName]], "readwrite");
  const store = transaction.objectStore(STORE_NAMES[storeName]);

  // uploadedImages
  if (storeName === "uploadedImages") {
    const uploadedImageIdsInUse = new Set();

    for (const item of cartItems) {
      if (Array.isArray(item.uploadedImageIds)) {
        item.uploadedImageIds.forEach(id => uploadedImageIdsInUse.add(id));
      }
    }

    return new Promise((resolve) => {
      const request = store.getAll();

      request.onsuccess = () => {
        const images = request.result || [];

        for (const image of images) {
          if (!uploadedImageIdsInUse.has(image.id)) {
            store.delete(image.id);
          }
        }

        resolve();
      };

      request.onerror = () => resolve();
    });
  }

  // resultImages / onProductImages
  if (storeName === "resultImages" || storeName === "onProductImages") {
    const cartImageIds = new Set(cartItems.map((item) => item.imageId).filter(Boolean));

    return new Promise((resolve) => {
      const request = store.getAll();

      request.onsuccess = async () => {
        const images = request.result || [];

        for (const image of images) {
          if (storeName === "onProductImages") {
            const isInCart = cartItems.some(
              (item) => item.id === image.productId && item.imageId === image.resultImageId
            );
            if (isInCart) continue;
          }

          if (!cartImageIds.has(image.id)) {
            store.delete(image.id);
          }
        }

        resolve();
      };

      request.onerror = () => {
        resolve();
      };
    });
  }
};
